import { ReactNode, useCallback, useState } from "react";
import * as React from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import ClearIcon from "@mui/icons-material/Clear";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import type { SxProps } from "@mui/material/styles";

import { get, kebabCase } from "lodash";

import { isBlank } from "utils/common";

export type OptionType = {
  value: any;
  label: string;
  object?: any;
  disabled?: boolean;
  // Optional heading: a heading is rendered above the first option of each run of options sharing
  // a `group`. Options without one render exactly as before.
  group?: string;
};
type SelectFormFieldProps = {
  options: OptionType[];
  name: string;
  label?: string | ReactNode;
  disabled?: boolean;
  required?: boolean;
  onBlur?: () => void;
  isLoading?: boolean;
  afterUpdate?: (
    value?: string | number | null,
    field?: string,
    object?: any
  ) => void;
  emptyText?: string;
  helperText?: ReactNode;
  sx?: SxProps;
  onChange?: () => void;
  onDelete?: (id: string | number) => void;
  addButton?: ReactNode;
  dropDownZIndex?: number;
  /** Hide the clear (×) button, for a required field whose options already include "none". */
  disableClear?: boolean;
};

export type RhfSelectFormFieldProps<T extends FieldValues> =
  SelectFormFieldProps & UseControllerProps<T>;

export default function SelectFormField<T extends FieldValues>({
  name,
  label = "",
  disabled = false,
  control,
  options,
  required = false,
  onBlur,
  afterUpdate,
  onDelete,
  emptyText,
  helperText,
  addButton,
  dropDownZIndex,
  isLoading,
  shouldUnregister = false,
  disableClear = false,
  sx
}: RhfSelectFormFieldProps<T>) {
  const [open, setOpen] = useState(false);
  const validate = useCallback(
    (value: any) =>
      Boolean(!required || (required && !isBlank(value))) ||
      `${label || "Selection"} is required`,
    [required, label]
  );

  const {
    field,
    formState: { errors }
  } = useController<T>({
    name,
    control,
    shouldUnregister,
    rules: {
      validate: validate,
      required: {
        value: required,
        message: `${label || "Selection"} is required`
      }
    }
  });

  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  return (
    <FormControl
      disabled={disabled}
      required={required}
      variant="outlined"
      fullWidth
      error={Boolean(errorMessage)}
      sx={sx}
    >
      <Box display="flex" alignItems="center" gap="4px">
        {Boolean(label) && (
          <InputLabel
            id={kebabCase(name)}
            disabled={disabled}
            shrink={Boolean(field.value) || open}
          >
            {label}
          </InputLabel>
        )}

        {Boolean(addButton) && <Box sx={{ pb: 0.5 }}>{addButton}</Box>}
      </Box>

      <Select
        labelId={kebabCase(name)}
        id={kebabCase(name)}
        {...field}
        onBlur={() => {
          field.onBlur();

          if (onBlur) {
            onBlur();
          }
        }}
        MenuProps={{
          style: { zIndex: dropDownZIndex ? dropDownZIndex : undefined }
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputProps={{ ref: field.ref }}
        IconComponent={() => (
          <Box display="flex" onClick={() => !disabled && setOpen(!open)}>
            {disabled || disableClear || !field.value ? undefined : (
              <IconButton
                size="small"
                sx={{ visibility: "visible" }}
                onClick={(e) => {
                  e.stopPropagation();
                  field.onChange("");
                  if (afterUpdate) {
                    afterUpdate("", name, null);
                  }
                  field.onBlur();
                  if (onBlur) {
                    onBlur();
                  }
                }}
              >
                <ClearIcon sx={{ width: 16, height: 16 }} />
              </IconButton>
            )}
            {open ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
          </Box>
        )}
        onChange={(event) => {
          field.onChange(event);
          field.onBlur();
          if (afterUpdate) {
            const selected = options?.find(
              (option) => option.value === event.target.value
            )?.object;
            afterUpdate(event.target.value, name, selected);
          }
        }}
      >
        {!required && (
          <MenuItem value="">
            <em>
              {!Boolean(emptyText) || options.length > 0 ? "None" : emptyText}
            </em>
          </MenuItem>
        )}
        {options.flatMap(
          ({ label, value, disabled: optionDisabled, group }, index) => [
            ...(group && group !== options[index - 1]?.group
              ? [
                  <ListSubheader
                    key={`group_${group}_${value}`}
                    sx={{ lineHeight: "32px", pt: 0.5 }}
                  >
                    {group}
                  </ListSubheader>
                ]
              : []),
            <MenuItem
              key={[value, label].join("_")}
              value={value}
              disabled={optionDisabled}
              sx={
                Boolean(onDelete)
                  ? {
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }
                  : {}
              }
            >
              {label}
              {open && Boolean(onDelete) && (
                <ListItemIcon>
                  <IconButton
                    disabled={isLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(value);
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress sx={{ width: 16, height: 16 }} />
                    ) : (
                      <ClearIcon sx={{ width: 16, height: 16 }} />
                    )}
                  </IconButton>
                </ListItemIcon>
              )}
            </MenuItem>
          ]
        )}
      </Select>
      {Boolean(errorMessage) ? (
        <FormHelperText error>{errorMessage}</FormHelperText>
      ) : (
        Boolean(helperText) && <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}
